<template>
  <div class="customer-groups">
    <v-layout row wrap justify-space-between align-center class="mb-2">
      <v-flex>
        <v-subheader class="pl-0 groups-title">
          <v-icon small class="mr-2">fas fa-users</v-icon>
          Grupos de clientes
        </v-subheader>
      </v-flex>
      <v-flex text-xs-right>
        <v-btn small depressed round class="k-orange-linear mr-0" @click="openCreateDialog">
          <v-icon small class="mr-1">fas fa-plus</v-icon>
          Agregar grupo
        </v-btn>
      </v-flex>
    </v-layout>

    <!-- Groups list with drag -->
    <div class="groups-scroll-container">
      <draggable
        v-model="groups"
        :options="{ filter: '.group-card__actions, .group-card__body', preventOnFilter: false }"
        :move="canMove"
        ghost-class="group-ghost"
        class="groups-list"
        @change="reassignPriorities"
      >
        <div v-for="(group, i) in groups" :key="group._localId" class="group-item">
          <div
            class="group-card"
            :class="{
              'group-card--expanded': isExpanded(group._localId),
              'group-card--default': group.is_default === 1,
            }"
          >
            <!-- Accordion header -->
            <div class="group-card__header" @click="toggleGroup(group._localId)">
              <div class="drag-handle group-card__drag" @click.stop>
                <v-icon small>fas fa-grip-vertical</v-icon>
              </div>

              <span class="group-card__name">{{ group.name }}</span>

              <span v-if="group.is_default === 1" class="default-badge">
                <v-icon x-small class="mr-1" style="font-size:10px">fas fa-lock</v-icon>
                Por defecto
              </span>

              <div class="group-card__filters-preview">
                <template v-if="group.is_default !== 1">
                  <v-chip
                    v-for="chip in getFilterPreview(group.filters)"
                    :key="chip"
                    small
                    disabled
                    class="filter-chip"
                  >{{ chip }}</v-chip>
                  <v-chip
                    v-if="getRemainingCount(group.filters) > 0"
                    small
                    disabled
                    class="filter-chip filter-chip--more"
                  >+{{ getRemainingCount(group.filters) }} más</v-chip>
                  <span v-if="getTotalConditions(group.filters) === 0" class="no-filters-hint">
                    Sin filtros
                  </span>
                </template>
                <span v-else class="no-filters-hint">Aplica al resto de clientes</span>
              </div>

              <div class="group-card__attempts">
                <v-icon x-small class="mr-1" style="font-size:11px">fas fa-phone-alt</v-icon>
                {{ group.min_attempts }}–{{ group.max_attempts }}
              </div>

              <div class="group-card__divider" />

              <div class="group-card__actions" @click.stop>
                <v-btn icon small flat class="ma-0" @click="editGroup(i)">
                  <v-icon small class="k-orange-linear-text">fas fa-pencil-alt</v-icon>
                </v-btn>
                <v-btn v-if="group.is_default !== 1" icon small flat class="ma-0" @click="deleteGroup(i)">
                  <v-icon small style="color:#e53935">fas fa-trash-alt</v-icon>
                </v-btn>
                <v-icon
                  small
                  class="expand-icon"
                  :class="{ 'expand-icon--open': isExpanded(group._localId) }"
                >fas fa-chevron-down</v-icon>
              </div>
            </div>

            <!-- Accordion body -->
            <v-expand-transition>
              <div v-show="isExpanded(group._localId)" class="group-card__body">
                <v-divider />
                <div class="group-card__body-inner">
                  <template v-if="group.is_default !== 1">
                    <filters-list
                      :default-filters="group.filters"
                      :showBtn="false"
                      :key="`${group._localId}-${group._version || 0}-view`"
                    />
                  </template>
                  <div v-else class="default-group-hint">
                    <v-icon small class="mr-2" style="color:#9e9e9e">fas fa-info-circle</v-icon>
                    Este grupo no tiene filtros y agrupa a todos los clientes que no pertenezcan a otro grupo.
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>
        </div>
      </draggable>
    </div>

    <!-- Create / Edit dialog -->
    <dialog-v2
      :show="dialog"
      :title="editIndex !== null ? 'Editar grupo' : 'Crear grupo de clientes'"
      max-width="820px"
      @close="closeDialog"
      @save="saveGroup"
    >
      <template slot="content">
        <v-layout row wrap>
          <v-flex xs12 md6 pr-3>
            <v-text-field
              v-model="form.name"
              label="Nombre del grupo"
              color="black"
              counter="200"
              :disabled="editingDefault"
            />
          </v-flex>
          <v-flex xs12 md3 pr-3>
            <v-text-field
              v-model.number="form.min_attempts"
              label="Int. mínimos"
              color="black"
              type="number"
              min="1"
            />
          </v-flex>
          <v-flex xs12 md3>
            <v-text-field
              v-model.number="form.max_attempts"
              label="Int. máximos"
              color="black"
              type="number"
              min="1"
            />
          </v-flex>
        </v-layout>
        <div v-if="attemptsError" class="filter-error-msg mb-2">
          <v-icon small class="mr-1" style="color:#e53935">fas fa-exclamation-circle</v-icon>
          Los intentos mínimos deben ser menores a los intentos máximos.
        </div>
        <filters-list
          v-if="!editingDefault"
          v-model="form.filters"
          :default-filters="form.filters"
          :showBtn="true"
          :key="dialogFiltersKey"
        />
        <div v-if="filterError" class="filter-error-msg">
          <v-icon small class="mr-1" style="color:#e53935">fas fa-exclamation-circle</v-icon>
          Debes agregar al menos un filtro al grupo.
        </div>
      </template>
    </dialog-v2>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import FiltersList from '@/components/global/CustomerFilters/Index';
import DialogV2 from '@/components/global/DialogV2';
import filtersMixin from '@/mixins/workflowFilters';
import comparisonOperatorsMixin from '@/mixins/comparisonOperators';

const uuidv1 = require('uuid/v1');

const emptyFilters = () => ({ type: 'group', operator: 'and', items: [] });

const makeDefaultGroup = () => ({
  _localId: uuidv1(),
  name: 'Sin grupo',
  priority: 1,
  min_attempts: 1,
  max_attempts: 15,
  filters: emptyFilters(),
  is_default: 1,
});

const ensureDefaultGroup = (groups) => {
  const mapped = groups.map(g => ({ ...g, _localId: g._localId || uuidv1() }));
  if (!mapped.some(g => g.is_default === 1)) {
    mapped.push(makeDefaultGroup());
  }
  const nonDefault = mapped.filter(g => g.is_default !== 1).sort((a, b) => a.priority - b.priority);
  const defaultGroup = mapped.find(g => g.is_default === 1);
  return [...nonDefault, defaultGroup].map((g, i) => ({ ...g, priority: i + 1 }));
};

export default {
  inject: ['$validator'],
  components: { draggable, FiltersList, DialogV2 },
  mixins: [filtersMixin, comparisonOperatorsMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      groups: ensureDefaultGroup(this.value),
      expandedGroups: {},
      dialog: false,
      editIndex: null,
      editingDefault: false,
      filterError: false,
      attemptsError: false,
      dialogFiltersKey: uuidv1(),
      form: {
        name: '',
        priority: 1,
        min_attempts: 1,
        max_attempts: 15,
        filters: emptyFilters(),
        is_default: 0,
      },
    };
  },
  methods: {
    toggleGroup(localId) {
      this.$set(this.expandedGroups, localId, !this.expandedGroups[localId]);
    },
    isExpanded(localId) {
      return !!this.expandedGroups[localId];
    },
    getTotalConditions(filters) {
      if (!filters || !filters.items) return 0;
      return filters.items.reduce((acc, g) => acc + (g.items ? g.items.length : 0), 0);
    },
    getFilterPreview(filters) {
      if (!filters || !filters.items) return [];
      const conditions = filters.items.reduce((acc, g) => acc.concat(g.items || []), []);
      return conditions.slice(0, 2).map(item => this.getfilterAndAmountTitle(item.field) || item.field);
    },
    getRemainingCount(filters) {
      return Math.max(0, this.getTotalConditions(filters) - 2);
    },
    canMove(evt) {
      return evt.draggedContext.element.is_default !== 1;
    },
    reassignPriorities() {
      const nonDefault = this.groups.filter(g => g.is_default !== 1);
      const defaultGroup = this.groups.find(g => g.is_default === 1);
      const ordered = defaultGroup ? [...nonDefault, defaultGroup] : nonDefault;
      this.groups = ordered.map((g, i) => ({ ...g, priority: i + 1 }));
    },
    openCreateDialog() {
      this.editIndex = null;
      this.editingDefault = false;
      this.form = {
        name: '',
        priority: this.groups.length + 1,
        min_attempts: 1,
        max_attempts: 15,
        filters: emptyFilters(),
        is_default: 0,
      };
      this.dialogFiltersKey = uuidv1();
      this.dialog = true;
    },
    editGroup(i) {
      this.editIndex = i;
      const g = this.groups[i];
      this.editingDefault = g.is_default === 1;
      this.form = {
        name: g.name,
        priority: g.priority,
        min_attempts: g.min_attempts,
        max_attempts: g.max_attempts,
        filters: JSON.parse(JSON.stringify(g.filters)),
        is_default: g.is_default || 0,
      };
      this.dialogFiltersKey = uuidv1();
      setTimeout(() => { this.dialog = true; }, 100);
    },
    deleteGroup(i) {
      if (this.groups[i].is_default === 1) return;
      this.groups.splice(i, 1);
      this.reassignPriorities();
    },
    saveGroup() {
      if (!this.form.name.trim()) return;
      if (!this.editingDefault && this.getTotalConditions(this.form.filters) === 0) {
        this.filterError = true;
        return;
      }
      this.filterError = false;
      if (this.form.min_attempts >= this.form.max_attempts) {
        this.attemptsError = true;
        return;
      }
      this.attemptsError = false;
      if (this.editIndex !== null) {
        const existing = this.groups[this.editIndex];
        this.$set(this.groups, this.editIndex, {
          ...existing,
          _version: (existing._version || 0) + 1,
          name: this.form.name,
          priority: this.form.priority,
          min_attempts: this.form.min_attempts,
          max_attempts: this.form.max_attempts,
          filters: this.form.filters,
        });
      } else {
        this.groups.push({
          _localId: uuidv1(),
          name: this.form.name,
          priority: this.form.priority,
          min_attempts: this.form.min_attempts,
          max_attempts: this.form.max_attempts,
          filters: this.form.filters,
          is_default: 0,
        });
        this.reassignPriorities();
      }
      this.closeDialog();
    },
    closeDialog() {
      this.dialog = false;
      this.filterError = false;
      this.attemptsError = false;
      setTimeout(() => {
        this.editIndex = null;
        this.editingDefault = false;
        this.form = { name: '', priority: 1, min_attempts: 1, max_attempts: 15, filters: emptyFilters(), is_default: 0 };
      }, 200);
    },
  },
  watch: {
    groups: {
      handler(val) {
        this.$emit('input', val);
      },
      deep: true,
    },
    value(val) {
      if (!val.length && this.groups.length) {
        this.groups = [makeDefaultGroup()];
      }
    },
  },
};
</script>

<style scoped>
.groups-title {
  font-weight: 600;
  font-size: 14px;
}

/* Scroll container */
.groups-scroll-container {
  max-height: 340px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}
.groups-scroll-container::-webkit-scrollbar {
  width: 4px;
}
.groups-scroll-container::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 4px;
}
.groups-scroll-container::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 4px;
}
.groups-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}

/* Draggable */
.groups-list { padding-bottom: 4px; }
.group-item { margin-bottom: 8px; }
.group-ghost {
  opacity: 0.45;
  border-radius: 8px;
  background: #fff3e0;
  box-shadow: 0 4px 16px rgba(240, 108, 37, 0.2);
}

/* Card */
.group-card {
  border: 1px solid #ebebeb;
  border-left: 3px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.18s ease, border-left-color 0.18s ease;
}
.group-card:hover {
  border-left-color: #f06c25;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
}
.group-card--expanded {
  border-left-color: #f06c25;
  box-shadow: 0 4px 14px rgba(240, 108, 37, 0.13);
}
.group-card--default {
  border-left-color: #9e9e9e;
}
.group-card--default:hover {
  border-left-color: #757575;
}
.group-card--default .group-card__header {
  cursor: default;
}
.group-card--default .drag-handle {
  opacity: 0.2;
  cursor: not-allowed;
}

/* Header */
.group-card__header {
  display: flex;
  align-items: center;
  padding: 11px 14px 11px 10px;
  gap: 10px;
  cursor: grab;
  background: #fff;
  user-select: none;
}
.group-card__header:active { cursor: grabbing; }
.group-card--expanded .group-card__header {
  background: #fffaf7;
}

/* Drag handle */
.drag-handle {
  color: #d0d0d0;
  flex-shrink: 0;
  padding: 2px 3px;
  transition: color 0.15s;
  pointer-events: none;
}
.group-card__header:hover .drag-handle { color: #9e9e9e; }

/* Name */
.group-card__name {
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
  flex-shrink: 0;
  min-width: 80px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Default badge */
.default-badge {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  color: #757575;
  background: #f0f0f0;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 2px 8px;
  flex-shrink: 0;
  white-space: nowrap;
}

/* Filter preview */
.group-card__filters-preview {
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  flex: 1;
  overflow: hidden;
  align-items: center;
}
.filter-chip {
  background-color: #f2f2f2 !important;
  color: #555 !important;
  font-size: 11px !important;
  height: 20px !important;
  border-radius: 4px !important;
}
.filter-chip--more {
  background-color: #eef2ff !important;
  color: #4361ee !important;
}
.no-filters-hint {
  font-size: 11px;
  color: #b0b0b0;
  font-style: italic;
}

/* Attempts badge */
.group-card__attempts {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 500;
  color: #666;
  white-space: nowrap;
  background: #f5f5f5;
  padding: 4px 10px;
  border-radius: 20px;
  flex-shrink: 0;
  gap: 4px;
}

/* Vertical divider */
.group-card__divider {
  width: 1px;
  height: 22px;
  background: #e8e8e8;
  flex-shrink: 0;
  margin: 0 2px;
}

/* Actions */
.group-card__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.expand-icon {
  transition: transform 0.22s ease;
  color: #c0c0c0 !important;
  margin-left: 2px;
}
.expand-icon--open {
  transform: rotate(180deg);
  color: #f06c25 !important;
}

/* Body */
.group-card__body { background: #fff; }
.group-card__body-inner { padding: 4px 16px 12px; }

.filter-error-msg {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #e53935;
  margin-top: 8px;
}

.default-group-hint {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #9e9e9e;
  padding: 8px 0 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/CallList/Modal/CustomerGroups.vue